import { useEffect, useState } from "react";
import SectionHeading from "./SectionHeading";
import { destinationApi } from "@/api/endpoints";
import { asArray } from "@/lib/asArray";
import type { Destination } from "@/types";

// Fallback used if the API isn't reachable yet, so the page still looks complete.
// Images are royalty-free Unsplash placeholders — swap for your own later.
const FALLBACK: Destination[] = [
  { public_id: "f1", name: "Jaipur", city: "Jaipur", tagline: "The Pink City", description: null, image_url: "https://images.unsplash.com/photo-1599661046289-e31897846e41?auto=format&fit=crop&w=800&q=70", ticket_price: 0, latitude: null, longitude: null },
  { public_id: "f2", name: "Udaipur", city: "Udaipur", tagline: "The City of Lakes", description: null, image_url: "https://images.unsplash.com/photo-1609920658906-8223bd289001?auto=format&fit=crop&w=800&q=70", ticket_price: 0, latitude: null, longitude: null },
  { public_id: "f3", name: "Jodhpur", city: "Jodhpur", tagline: "The Blue City", description: null, image_url: "https://images.unsplash.com/photo-1477587458883-47145ed94245?auto=format&fit=crop&w=800&q=70", ticket_price: 0, latitude: null, longitude: null },
  { public_id: "f4", name: "Jaisalmer", city: "Jaisalmer", tagline: "The Golden City", description: null, image_url: "https://images.unsplash.com/photo-1590766940554-153a4d9f6821?auto=format&fit=crop&w=800&q=70", ticket_price: 0, latitude: null, longitude: null },
  { public_id: "f5", name: "Pushkar", city: "Pushkar", tagline: "The Holy City", description: null, image_url: "https://images.unsplash.com/photo-1572445271230-a78b5944a659?auto=format&fit=crop&w=800&q=70", ticket_price: 0, latitude: null, longitude: null },
];

export default function PopularDestinations() {
  const [items, setItems] = useState<Destination[]>(FALLBACK);

  useEffect(() => {
    let active = true;
    destinationApi
      .list(true)
      .then((res) => {
        const data = asArray<Destination>(res.data);
        if (active && data.length) setItems(data);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  return (
    <section id="destinations" className="bg-cream py-20">
      <div className="container-page">
        <SectionHeading title="Featured Destinations" action="View All Destinations →" />
        <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {items.slice(0, 5).map((d, i) => (
            <a
              key={d.public_id}
              href="/#destinations"
              className="group relative block h-64 overflow-hidden rounded-2xl shadow-card"
            >
              <img
                src={d.image_url ?? ""}
                alt={`${d.name} — ${d.tagline ?? ""}`}
                loading={i < 2 ? "eager" : "lazy"}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-900/85 via-navy-900/10 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-4">
                <h3 className="font-display text-lg font-semibold text-white">
                  {d.name}
                </h3>
                {d.tagline && <p className="text-xs text-white/80">{d.tagline}</p>}
                <span className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-gold-400">
                  Explore →
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
